import React, { useState, useEffect } from 'react';
import { DeliveryPerson, DeliveryPersonDisplayInfo } from '../types';
import { DeliveryPersonService } from '../services/deliveryPersonService';
import LoadingSpinner from './LoadingSpinner';

interface DeliveryPersonSelectorProps {
  selectedDeliveryHash?: string;
  onSelect: (deliveryHash: string | null, displayInfo?: DeliveryPersonDisplayInfo) => void;
  disabled?: boolean;
  placeholder?: string;
  showClearButton?: boolean;
}

const DeliveryPersonSelector: React.FC<DeliveryPersonSelectorProps> = ({
  selectedDeliveryHash,
  onSelect,
  disabled = false,
  placeholder = "Select delivery person...",
  showClearButton = true
}) => {
  const [deliveryPersons, setDeliveryPersons] = useState<DeliveryPerson[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  // Load delivery persons on component mount
  useEffect(() => {
    loadDeliveryPersons();
  }, []);

  const loadDeliveryPersons = async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await DeliveryPersonService.getActiveDeliveryPersons(1, 100);
      
      if (result.error) {
        setError(result.error);
        setDeliveryPersons([]);
      } else {
        setDeliveryPersons(result.data || []);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load delivery persons');
      setDeliveryPersons([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (person: DeliveryPerson | null) => {
    if (person) {
      const displayInfo = DeliveryPersonService.getDeliveryPersonDisplayInfo(person);
      onSelect(person.delivery_hash, displayInfo);
    } else {
      onSelect(null);
    }
    setIsOpen(false);
  };

  const selectedPerson = deliveryPersons.find(p => p.delivery_hash === selectedDeliveryHash);
  const selectedDisplayName = selectedPerson ? 
    DeliveryPersonService.formatDeliveryPersonName(selectedPerson) : 
    placeholder;

  if (loading) {
    return (
      <div className="relative">
        <div className="flex items-center justify-between w-full px-3 py-2 text-left bg-white border border-gray-300 rounded-md shadow-sm cursor-not-allowed opacity-50">
          <span className="text-gray-500">Loading delivery persons...</span>
          <LoadingSpinner size="sm" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="relative">
        <div className="flex items-center justify-between w-full px-3 py-2 text-left bg-red-50 border border-red-300 rounded-md shadow-sm">
          <span className="text-red-600 text-sm">Error: {error}</span>
          <button
            onClick={loadDeliveryPersons}
            className="ml-2 text-red-600 hover:text-red-800"
            title="Retry loading"
          >
            🔄
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Main selector button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          flex items-center justify-between w-full px-3 py-2 text-left bg-white border border-gray-300 rounded-md shadow-sm
          ${disabled 
            ? 'cursor-not-allowed opacity-50' 
            : 'hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
          }
          ${selectedPerson ? 'text-gray-900' : 'text-gray-500'}
        `}
      >
        <div className="flex items-center space-x-2">
          {selectedPerson?.avatar_url && (
            <img
              src={selectedPerson.avatar_url}
              alt={selectedPerson.name}
              className="w-6 h-6 rounded-full object-cover"
            />
          )}
          <span className="truncate">{selectedDisplayName}</span>
        </div>
        
        <div className="flex items-center space-x-1">
          {showClearButton && selectedPerson && !disabled && (
            <div
              onClick={(e) => {
                e.stopPropagation();
                handleSelect(null);
              }}
              className="text-gray-400 hover:text-gray-600 p-1 cursor-pointer"
              title="Clear selection"
            >
              ✕
            </div>
          )}
          <svg
            className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Dropdown menu */}
      {isOpen && !disabled && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
          {deliveryPersons.length === 0 ? (
            <div className="px-3 py-2 text-gray-500 text-sm">
              No active delivery persons found
            </div>
          ) : (
            <>
              {/* Clear option */}
              <button
                onClick={() => handleSelect(null)}
                className="flex items-center w-full px-3 py-2 text-left hover:bg-gray-100 text-gray-500"
              >
                <span>None (Clear selection)</span>
              </button>
              
              {/* Delivery persons list */}
              {deliveryPersons.map((person) => (
                <button
                  key={person.delivery_hash}
                  onClick={() => handleSelect(person)}
                  className={`
                    flex items-center w-full px-3 py-2 text-left hover:bg-gray-100
                    ${person.delivery_hash === selectedDeliveryHash ? 'bg-blue-50 text-blue-700' : 'text-gray-900'}
                  `}
                >
                  <div className="flex items-center space-x-3 w-full">
                    {person.avatar_url ? (
                      <img
                        src={person.avatar_url}
                        alt={person.name}
                        className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center flex-shrink-0">
                        <span className="text-gray-600 text-sm font-medium">
                          {person.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                    
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">
                        {person.name}
                      </div>
                      <div className="text-sm text-gray-500 truncate">
                        Code: {person.code}
                        {person.phone_number && ` • ${person.phone_number}`}
                      </div>
                    </div>

                    {person.delivery_hash === selectedDeliveryHash && (
                      <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default DeliveryPersonSelector;
